// src/pages/Tower/TowerPage.jsx
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import FiltrosPanel, { CAMPO } from '../../components/FiltrosPanel';
import PaginacaoFooter from '../../components/PaginacaoFooter';
import { useNavigate, useParams } from 'react-router-dom';
import { alpha } from '@mui/material/styles';
import {
    Box,
    Typography,
    TextField,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Grid,
    InputAdornment,
    CircularProgress,
    useTheme,
    useMediaQuery,
    Skeleton,
    Stack,
    Button,
    Chip,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Stepper,
    Step,
    StepLabel,
    StepContent,
    IconButton,
} from '@mui/material';
import * as Lucide from 'lucide-react';

import TowerItem from '../../components/TowerItem';
import { getTowers, searchTowers } from '../../infra/api/Torres';
import { useDebounce } from '../../hooks/useDebounce';
import { useBootstrap } from '../../context/BootstrapContext';
import { SnackbarAlert } from '../../components/SnackbarAlert';
import useTenantTelemetry from '../../hooks/useTenantTelemetry';
import towerBlacklist from '../../config/towerBlacklist.json';
import LedStatusGuideDialog from '../../components/LedStatusGuideDialog';

import Permitido from '../../security/Permitido';

// -------------------------
// OPTIONS
// -------------------------
const STATUS_OPTIONS = [
    { value: 'ALL', label: 'Todos' },
    { value: 'PENDENTE', label: 'Pendente' },
    { value: 'LIBERADA', label: 'Liberado' },
    { value: 'ONLINE', label: 'Ativo' },
    { value: 'BLOQUEADA', label: 'Bloqueado' },
    { value: 'DEFEITO', label: 'Defeito' },
    { value: 'MANUTENCAO', label: 'Manutenção' },
    { value: 'NEW', label: 'Novo' },
];

const EXPERIMENT_SORT_OPTIONS = [
    { value: 'PRIORITY', label: 'Prioridade: Amostra em análise' }, // TEXTO ATUALIZADO AQUI
    { value: 'RUNNING', label: 'Apenas Amostra em análise' }, // TEXTO ATUALIZADO AQUI
    { value: 'IDLE', label: 'Apenas Sem Experimento' },
    { value: 'DEFAULT', label: 'Padrão (Sem ordenação)' },
];

// ... (Restante do código de TowerPage.jsx inalterado) ...

// -------------------------
// HELPERS
// -------------------------
const normalizeId = (val) => {
    if (!val) return '';
    if (typeof val === 'string') {
        const m = val.match(/ObjectId\("([^"]+)"\)/);
        if (m) return m[1];
        try {
            const parsed = JSON.parse(val);
            if (parsed?.$oid) return String(parsed.$oid);
        } catch (e) {}
        return val;
    }
    if (typeof val === 'object' && val?.$oid) return String(val.$oid);
    return String(val);
};

// -------------------------
// COMPONENTE
// -------------------------
const TowerPage = () => {
    const navigate = useNavigate();
    const { tenant } = useParams();

    const [towers, setTowers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [page, setPage] = useState(1);
    const [itemsPerPage, setItemsPerPage] = useState(10);

    const [searchQuery, setSearchQuery] = useState('');
    const debouncedSearchQuery = useDebounce(searchQuery, 500);

    const [statusFilter, setStatusFilter] = useState('ALL');
    const [experimentSort, setExperimentSort] = useState('PRIORITY');

    const [isSearching, setIsSearching] = useState(false);
    const [pageSnackbar, setPageSnackbar] = useState({
        open: false,
        message: '',
        severity: 'info',
    });

    const [targetSerial, setTargetSerial] = useState(null);

    const [helpOpen, setHelpOpen] = useState(false);
    const [protocolHelpOpen, setProtocolHelpOpen] = useState(false);

    const { bootstrap } = useBootstrap();
    const protocols = useMemo(() => bootstrap?.protocolos ?? [], [bootstrap]);
    const clientName = useMemo(() => bootstrap?.client?.name, [bootstrap]);
    const clientId = useMemo(() => bootstrap?.client?.id, [bootstrap]);

    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    const { devicesBySerial, connected, lastUpdated } = useTenantTelemetry();

    const isSearchMode = searchQuery.trim().length > 0;

    const loadTowers = useCallback(async () => {
        if (debouncedSearchQuery) return;
        setLoading(true);
        setError(null);
        try {
            const response = await getTowers(0, 2000);
            setTowers(response.content || response || []);
        } catch {
            setError('Falha ao carregar os dispositivos.');
            setPageSnackbar({
                open: true,
                message: 'Falha ao carregar os dispositivos.',
                severity: 'error',
            });
            setTowers([]);
        } finally {
            setLoading(false);
        }
    }, [debouncedSearchQuery]);

    const executeSearch = useCallback(async () => {
        if (!debouncedSearchQuery) {
            loadTowers();
            return;
        }
        setIsSearching(true);
        setLoading(true);
        setError(null);
        try {
            const results = await searchTowers(debouncedSearchQuery);
            setTowers(results || []);
            setPage(1);
        } catch {
            setError('Falha ao buscar pelo dispositivo.');
            setPageSnackbar({
                open: true,
                message: 'Nenhum dispositivo encontrado.',
                severity: 'warning',
            });
            setTowers([]);
        } finally {
            setIsSearching(false);
            setLoading(false);
        }
    }, [debouncedSearchQuery, loadTowers]);

    useEffect(() => {
        executeSearch();
    }, [executeSearch]);

    // Navegação real (não estado local) — o serial fica na URL, então um F5
    // na página do dashboard mantém o usuário lá em vez de voltar pra lista.
    // Precisa incluir o :tenant explicitamente — toda rota do app vive sob
    // /:tenant/* (App.jsx), então um path absoluto sem o tenant faz o router
    // reinterpretar "bioailabs" como se fosse o tenant (já causou login
    // quebrado no Keycloak apontando pro realm errado).
    const handleOpenDashboard = (serial) => {
        navigate(`/${tenant}/bioailabs/device/${serial}/dashboard`);
    };

    const processedTowers = useMemo(() => {
        let data = [...(towers || [])];

        if (clientId && towerBlacklist[clientId]) {
            const blacklist = towerBlacklist[clientId];
            data = data.filter(
                (t) => !blacklist.includes(String(t.numeroSerie || '').trim())
            );
        }

        if (!isSearchMode && statusFilter !== 'ALL') {
            data = data.filter(
                (t) => String(t?.status || '').toUpperCase() === statusFilter
            );
        }

        const isRunning = (tower) => {
            const serial = String(tower?.numeroSerie ?? '').trim();
            const live = devicesBySerial?.[serial];
            if (live) return !!live.experimentId;
            return tower?.isExperimentoRodando;
        };

        if (experimentSort === 'PRIORITY') {
            data.sort((a, b) =>
                isRunning(a) === isRunning(b) ? 0 : isRunning(a) ? -1 : 1
            );
        } else if (experimentSort === 'RUNNING') {
            data = data.filter((t) => isRunning(t));
        } else if (experimentSort === 'IDLE') {
            data = data.filter((t) => !isRunning(t));
        }

        return data;
    }, [
        towers,
        statusFilter,
        isSearchMode,
        experimentSort,
        devicesBySerial,
        clientId,
    ]);

    const totalPages = Math.max(
        1,
        Math.ceil(processedTowers.length / itemsPerPage)
    );

    const paginatedTowers = processedTowers.slice(
        (page - 1) * itemsPerPage,
        page * itemsPerPage
    );

    useEffect(() => {
        setPage(1);
    }, [statusFilter, experimentSort, debouncedSearchQuery]);

    const handleSearchChange = (e) => {
        const newQuery = e.target.value;
        setSearchQuery(newQuery);
        if (newQuery.trim()) {
            setStatusFilter('ALL');
        }
    };

    const filtrosAtivos =
        (searchQuery.trim() ? 1 : 0) +
        (statusFilter !== 'ALL' ? 1 : 0) +
        (experimentSort !== 'PRIORITY' ? 1 : 0);

    const handleLimparFiltros = () => {
        setSearchQuery('');
        setStatusFilter('ALL');
        setExperimentSort('PRIORITY');
        setPage(1);
    };

    const handleActionSuccess = (serial) => {
        if (serial) setTargetSerial(serial);
        if (searchQuery) executeSearch();
        else loadTowers();
    };

    const handleItemsPerPageChange = (valor) => {
        setItemsPerPage(valor);
        setPage(1);
    };

    // -------------------------------------------------------------
    // TELA PRINCIPAL (Lista de Dispositivos)
    // O dashboard de um dispositivo específico agora é uma rota própria
    // (/bioailabs/device/:serialNumber/dashboard — ver InnerRoutes.jsx e
    // handleOpenDashboard acima), não mais um branch de render aqui dentro.
    // -------------------------------------------------------------
    return (
        <Box
            sx={{
                p: { xs: 1.5, md: 2 },
                pb: { xs: 4, md: 4 },
                width: '100%',
                minHeight: '100%',
                bgcolor: 'background.default',
            }}
        >
            {/* Cabecalho: titulo e subtitulo saem daqui — a navbar ja identifica a
                pagina. Restam as acoes, alinhadas a direita no desktop e ocupando a
                largura no mobile. */}
            <Stack
                direction={{ xs: 'column', md: 'row' }}
                justifyContent="flex-end"
                alignItems={{ xs: 'stretch', md: 'center' }}
                mb={2}
                spacing={2}
            >

                <Stack
                    direction="row"
                    spacing={1}
                    alignItems="center"
                    sx={{ width: isMobile ? '100%' : 'auto' }}
                >
                    {/* Indicador compacto de conexão em tempo real (movido pra cá,
                        ao lado dos botões, pra poupar espaço) */}
                    <Permitido
                        cap="telemetria.ver"
                        fallback={
                            <Chip
                                size="small"
                                icon={<Lucide.Lock size={14} />}
                                label="Rede"
                                sx={{
                                    bgcolor: 'action.hover',
                                    color: 'text.disabled',
                                    fontWeight: 500,
                                }}
                            />
                        }
                    >
                        <Box
                            title={
                                connected && lastUpdated
                                    ? `Conexão em tempo real ativa — atualizado: ${lastUpdated.toLocaleTimeString()}`
                                    : connected
                                    ? 'Conexão em tempo real ativa'
                                    : 'Desconectado — os dados podem estar desatualizados'
                            }
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                alignSelf: 'stretch', // mesma altura dos botões
                                gap: 0.75,
                                px: 1.25,
                                mr: { xs: 0.5, sm: 1 }, // um pouco mais afastado dos botões
                                borderRadius: 2,
                                border: '1px solid',
                                borderColor: connected ? 'success.light' : 'divider',
                                bgcolor: connected
                                    ? (t) => alpha(t.palette.success.main, 0.12)
                                    : 'action.selected',
                            }}
                        >
                            <Box sx={{ position: 'relative', display: 'flex' }}>
                                <Box
                                    sx={{
                                        width: 9,
                                        height: 9,
                                        borderRadius: '50%',
                                        bgcolor: connected ? 'success.main' : 'grey.500',
                                        zIndex: 1,
                                    }}
                                />
                                {connected && (
                                    <Box
                                        sx={{
                                            position: 'absolute',
                                            width: 9,
                                            height: 9,
                                            borderRadius: '50%',
                                            bgcolor: 'success.main',
                                            opacity: 0.7,
                                            animation:
                                                'ping 1.5s cubic-bezier(0, 0, 0.2, 1) infinite',
                                            '@keyframes ping': {
                                                '75%, 100%': {
                                                    transform: 'scale(2)',
                                                    opacity: 0,
                                                },
                                            },
                                        }}
                                    />
                                )}
                            </Box>
                            <Typography
                                variant="caption"
                                fontWeight={700}
                                noWrap
                                sx={{
                                    // no celular esconde o texto (fica só a bolinha) p/ não apertar
                                    display: { xs: 'none', sm: 'block' },
                                    color: connected
                                        ? (t) =>
                                              t.palette.mode === 'dark'
                                                  ? '#fff'
                                                  : t.palette.success.dark
                                        : 'text.secondary',
                                }}
                            >
                                {connected ? 'Tempo Real' : 'Desconectado'}
                            </Typography>
                        </Box>
                    </Permitido>
                    <Button
                        variant="contained"
                        color="primary"
                        onClick={() => setProtocolHelpOpen(true)}
                        startIcon={<Lucide.HelpCircle size={18} />}
                        sx={{
                            borderRadius: 2,
                            textTransform: 'none',
                            fontWeight: 'bold',
                            flex: isMobile ? 1 : 'none',
                            boxShadow: theme.shadows[3],
                            background: `linear-gradient(45deg, ${theme.palette.primary.main} 30%, ${theme.palette.primary.dark} 90%)`,
                        }}
                    >
                        Protocolos
                    </Button>
                    <Button
                        variant="contained"
                        color="info"
                        onClick={() => setHelpOpen(true)}
                        startIcon={<Lucide.HelpCircle size={18} />}
                        sx={{
                            borderRadius: 2,
                            textTransform: 'none',
                            fontWeight: 'bold',
                            flex: isMobile ? 1 : 'none',
                            boxShadow: theme.shadows[3],
                            background: `linear-gradient(45deg, ${theme.palette.info.main} 30%, ${theme.palette.info.dark} 90%)`,
                        }}
                    >
                        LEDs
                    </Button>
                </Stack>
            </Stack>

            {/* Status de conexão em tempo real agora fica compacto no header,
                ao lado dos botões (ver Stack acima) — poupa espaço. */}

            <Box
                sx={{
                    mb: 3,
                    borderRadius: 2,
                    border: '1px solid',
                    borderColor: 'grey.400',
                    overflow: 'hidden',
                }}
            >
            <FiltrosPanel
                defaultExpanded
                ativos={filtrosAtivos}
                loading={loading}
                onLimpar={handleLimparFiltros}
            >
                        <Grid item {...CAMPO}>
                            <TextField
                                fullWidth
                                label="Buscar BioAILab"
                                variant="outlined"
                                size="small"
                                value={searchQuery}
                                onChange={handleSearchChange}
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <Lucide.Search size={16} />
                                        </InputAdornment>
                                    ),
                                    endAdornment: isSearching && (
                                        <CircularProgress
                                            color="inherit"
                                            size={20}
                                        />
                                    ),
                                }}
                            />
                        </Grid>
                        <Grid item {...CAMPO}>
                            <FormControl
                                fullWidth
                                size="small"
                                disabled={loading}
                            >
                                <InputLabel>Visualização</InputLabel>
                                <Select
                                    label="Visualização"
                                    value={experimentSort}
                                    onChange={(e) =>
                                        setExperimentSort(e.target.value)
                                    }
                                >
                                    {EXPERIMENT_SORT_OPTIONS.map((opt) => (
                                        <MenuItem
                                            key={opt.value}
                                            value={opt.value}
                                        >
                                            {opt.label}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid item {...CAMPO}>
                            <FormControl
                                fullWidth
                                size="small"
                                disabled={isSearchMode || loading}
                            >
                                <InputLabel>Status</InputLabel>
                                <Select
                                    label="Status"
                                    value={statusFilter}
                                    onChange={(e) =>
                                        setStatusFilter(e.target.value)
                                    }
                                >
                                    {STATUS_OPTIONS.map((opt) => (
                                        <MenuItem
                                            key={opt.value}
                                            value={opt.value}
                                        >
                                            {opt.label}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>
            </FiltrosPanel>
            </Box>

            <Grid container spacing={3}>
                {loading ? (
                    Array.from({ length: itemsPerPage }).map((_, i) => (
                        <Grid item xs={12} md={6} key={i}>
                            <Skeleton
                                variant="rectangular"
                                height={180}
                                sx={{ borderRadius: 2 }}
                            />
                        </Grid>
                    ))
                ) : paginatedTowers.length === 0 ? (
                    <Grid item xs={12}>
                        <Stack
                            sx={{
                                textAlign: 'center',
                                p: 4,
                                alignItems: 'center',
                            }}
                            spacing={2}
                        >
                            <Lucide.SearchX
                                size={64}
                                color={theme.palette.text.secondary}
                            />
                            <Typography
                                variant="h6"
                                sx={{ fontWeight: 'bold' }}
                            >
                                Nenhum dispositivo encontrado
                            </Typography>
                            <Typography variant="body1" color="text.secondary">
                                Ajuste sua busca ou filtros.
                            </Typography>
                        </Stack>
                    </Grid>
                ) : (
                    paginatedTowers.map((tower) => (
                        <Grid
                            item
                            xs={12}
                            md={6}
                            key={normalizeId(tower.id || tower._id)}
                        >
                            <TowerItem
                                tower={tower}
                                protocols={protocols}
                                clientName={clientName}
                                live={
                                    devicesBySerial?.[
                                        String(tower?.numeroSerie ?? '').trim()
                                    ]
                                }
                                onActionSuccess={handleActionSuccess}
                                onShowSnackbar={setPageSnackbar}
                                allTowers={processedTowers}
                                onOpenDashboard={handleOpenDashboard}
                            />
                        </Grid>
                    ))
                )}
            </Grid>

            <PaginacaoFooter
                page={page}
                totalPages={totalPages}
                onPageChange={setPage}
                itemsPerPage={itemsPerPage}
                onItemsPerPageChange={handleItemsPerPageChange}
                itemsPerPageOptions={[5, 10, 20]}
                loading={loading}
            />

            <SnackbarAlert
                open={pageSnackbar.open}
                onClose={() =>
                    setPageSnackbar((prev) => ({ ...prev, open: false }))
                }
                message={pageSnackbar.message}
                severity={pageSnackbar.severity}
            />

            {/* MODAIS DE AJUDA */}
            <LedStatusGuideDialog
                open={helpOpen}
                onClose={() => setHelpOpen(false)}
            />

            <Dialog
                open={protocolHelpOpen}
                onClose={() => setProtocolHelpOpen(false)}
                fullWidth
                maxWidth="sm"
                PaperProps={{ sx: { borderRadius: 3, m: 2 } }}
            >
                <DialogTitle sx={{ pb: 1 }}>
                    <Box
                        display="flex"
                        alignItems="center"
                        justifyContent="space-between"
                    >
                        <Box display="flex" alignItems="center" gap={1}>
                            <Lucide.HelpCircle color={theme.palette.primary.main} />
                            <Typography variant="h6" fontWeight="bold">
                                Como alterar o Protocolo?
                            </Typography>
                        </Box>
                        <IconButton
                            size="small"
                            onClick={() => setProtocolHelpOpen(false)}
                        >
                            <Lucide.X size={20} />
                        </IconButton>
                    </Box>
                </DialogTitle>
                <DialogContent dividers sx={{ p: { xs: 2, sm: 4 } }}>
                    <Typography variant="body2" color="text.secondary" mb={3}>
                        Siga o passo a passo para alterar o protocolo que o
                        BioAILab executará no próximo ciclo de análise.
                    </Typography>
                    <Stepper orientation="vertical">
                        <Step active={true}>
                            <StepLabel
                                StepIconComponent={() => (
                                    <Lucide.Search color={theme.palette.primary.main} size={22} />
                                )}
                            >
                                <Typography fontWeight="bold">
                                    1. Encontre o BioAILab
                                </Typography>
                            </StepLabel>
                            <StepContent>
                                <Typography
                                    variant="body2"
                                    color="text.secondary"
                                >
                                    Navegue pela lista abaixo ou utilize o campo{' '}
                                    <b>"Buscar BioAILab"</b> para localizar o
                                    dispositivo.
                                </Typography>
                            </StepContent>
                        </Step>
                        <Step active={true}>
                            <StepLabel
                                StepIconComponent={() => (
                                    <Lucide.Ban color={theme.palette.error.main} size={22} />
                                )}
                            >
                                <Typography fontWeight="bold">
                                    2. Verifique se o Dispositivo está Livre
                                </Typography>
                            </StepLabel>
                            <StepContent>
                                <Typography
                                    variant="body2"
                                    color="text.secondary"
                                >
                                    <span
                                        style={{
                                            color: '#d32f2f',
                                            fontWeight: 'bold',
                                        }}
                                    >
                                        Importante:
                                    </span>{' '}
                                    O protocolo{' '}
                                    <b>
                                        só pode ser alterado se não houver um
                                        experimento em andamento
                                    </b>
                                    .
                                </Typography>
                            </StepContent>
                        </Step>
                        <Step active={true}>
                            <StepLabel
                                StepIconComponent={() => (
                                    <Lucide.FlaskConical color={theme.palette.secondary.main} size={22} />
                                )}
                            >
                                <Typography fontWeight="bold">
                                    3. Selecione o Novo Protocolo
                                </Typography>
                            </StepLabel>
                            <StepContent>
                                <Typography
                                    variant="body2"
                                    color="text.secondary"
                                >
                                    No card do dispositivo, localize o campo
                                    "Protocolo" (ícone de frasco) e clique para
                                    abrir a lista.
                                </Typography>
                            </StepContent>
                        </Step>
                        <Step active={true}>
                            <StepLabel
                                StepIconComponent={() => (
                                    <Lucide.CheckCircle2 color={theme.palette.success.main} size={22} />
                                )}
                            >
                                <Typography fontWeight="bold">
                                    4. Confirme a Alteração
                                </Typography>
                            </StepLabel>
                            <StepContent>
                                <Typography
                                    variant="body2"
                                    color="text.secondary"
                                >
                                    Após selecionar, a alteração será gravada e
                                    baixada automaticamente na próxima rotina.
                                </Typography>
                            </StepContent>
                        </Step>
                    </Stepper>
                </DialogContent>
                <DialogActions sx={{ p: 2, bgcolor: 'background.default' }}>
                    <Button
                        onClick={() => setProtocolHelpOpen(false)}
                        variant="contained"
                        fullWidth={isMobile}
                        disableElevation
                    >
                        Entendi
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default TowerPage;
